import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useMinStaffing,
  useReplaceMinStaffing,
  type MinStaffingRuleInput,
} from '@/hooks/optimization/useOptimizationSettings';
import { useLocationPositions } from '@/hooks/optimization/useLocationPositions';

interface MinStaffingTabProps {
  locationId: string | null;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const ALL_DAYS = 'all';

interface RowDraft {
  key: string;
  position_id: string;
  day_of_week: number | null;
  hour_from: number;
  hour_to: number;
  min_employees: number;
}

/**
 * Минимальный штат локации: несжимаемый минимум людей по должности и часам.
 * Солвер обязан соблюдать этот floor — он не «торгуется» против экономии ФОТ.
 * Сохранение — одним PUT (полная замена набора правил).
 */
export const MinStaffingTab: React.FC<MinStaffingTabProps> = ({
  locationId,
}) => {
  const { data: rules, isLoading } = useMinStaffing(locationId);
  const { positions } = useLocationPositions(locationId);
  const replace = useReplaceMinStaffing(locationId);

  const [rows, setRows] = useState<RowDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Синхронизация с сервером, пока пользователь не начал редактировать
  useEffect(() => {
    if (!dirty && rules) {
      setRows(
        rules.map((r) => ({
          key: r.id,
          position_id: r.position_id,
          day_of_week: r.day_of_week,
          hour_from: r.hour_from,
          hour_to: r.hour_to,
          min_employees: r.min_employees,
        })),
      );
    }
  }, [rules, dirty]);

  if (!locationId) {
    return (
      <p className="text-sm text-muted-foreground">
        Выберите локацию, чтобы настроить минимальный штат.
      </p>
    );
  }

  const patchRow = (key: string, patch: Partial<RowDraft>) => {
    setDirty(true);
    setSavedAt(null);
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  };

  const addRow = () => {
    setDirty(true);
    setSavedAt(null);
    setRows((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}-${prev.length}`,
        position_id: positions[0]?.id ?? '',
        day_of_week: null,
        hour_from: 9,
        hour_to: 22,
        min_employees: 1,
      },
    ]);
  };

  const removeRow = (key: string) => {
    setDirty(true);
    setSavedAt(null);
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const validate = (): string => {
    for (const r of rows) {
      if (!r.position_id) return 'У каждого правила должна быть должность';
      if (r.hour_from >= r.hour_to)
        return `Некорректный диапазон часов ${r.hour_from}–${r.hour_to}: начало должно быть меньше конца`;
      if (r.min_employees < 0) return 'Минимум людей не может быть отрицательным';
    }
    return '';
  };

  const save = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError('');
    const payload: MinStaffingRuleInput[] = rows.map((r) => ({
      position_id: r.position_id,
      day_of_week: r.day_of_week,
      hour_from: r.hour_from,
      hour_to: r.hour_to,
      min_employees: r.min_employees,
    }));
    try {
      await replace.mutateAsync(payload);
      setDirty(false);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    }
  };

  const headcountOf = (positionId: string) =>
    positions.find((p) => p.id === positionId)?.employee_count ?? null;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-start gap-4">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Несжимаемый минимум людей для работы точки — например «с открытия до
          закрытия минимум 1 кассир и 1 повар». Солвер обязан соблюдать этот
          минимум независимо от прогноза выручки и цели ФОТ. «Все дни» —
          правило действует ежедневно; выберите день недели для особых дней.
        </p>
        <Button size="sm" onClick={addRow} disabled={positions.length === 0}>
          <Plus className="h-4 w-4 mr-2" />
          Добавить правило
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Правила минимального штата не заданы — солвер может оставить точку
          без людей в слабые часы. Рекомендуем задать минимум для ключевых
          должностей.
        </p>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Должность</TableHead>
                <TableHead>День недели</TableHead>
                <TableHead className="w-[90px]">С часа</TableHead>
                <TableHead className="w-[90px]">До часа</TableHead>
                <TableHead className="w-[110px]">Минимум, чел</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const headcount = headcountOf(r.position_id);
                const overHeadcount =
                  headcount !== null && r.min_employees > headcount;
                return (
                  <TableRow key={r.key}>
                    <TableCell>
                      <Select
                        value={r.position_id}
                        onValueChange={(v) =>
                          patchRow(r.key, { position_id: v })
                        }
                      >
                        <SelectTrigger className="w-[220px]">
                          <SelectValue placeholder="Должность" />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.employee_count} чел)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={
                          r.day_of_week === null
                            ? ALL_DAYS
                            : String(r.day_of_week)
                        }
                        onValueChange={(v) =>
                          patchRow(r.key, {
                            day_of_week: v === ALL_DAYS ? null : Number(v),
                          })
                        }
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL_DAYS}>Все дни</SelectItem>
                          {WEEKDAYS.map((d, i) => (
                            <SelectItem key={d} value={String(i)}>
                              {d}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        max={23}
                        value={r.hour_from}
                        onChange={(e) =>
                          patchRow(r.key, {
                            hour_from: parseInt(e.target.value, 10) || 0,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        max={24}
                        value={r.hour_to}
                        onChange={(e) =>
                          patchRow(r.key, {
                            hour_to: parseInt(e.target.value, 10) || 0,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        value={r.min_employees}
                        className={overHeadcount ? 'border-amber-500' : ''}
                        onChange={(e) =>
                          patchRow(r.key, {
                            min_employees: parseInt(e.target.value, 10) || 0,
                          })
                        }
                      />
                      {overHeadcount && (
                        <p className="text-xs text-amber-600 mt-1">
                          В штате всего {headcount} чел
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRow(r.key)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || replace.isPending}>
          {replace.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : null}
          Сохранить
        </Button>
        {savedAt && !dirty && (
          <span className="text-sm text-green-700">Сохранено</span>
        )}
        {dirty && (
          <span className="text-sm text-muted-foreground">
            Есть несохранённые изменения
          </span>
        )}
      </div>
    </div>
  );
};
